import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Users, Building2, Package, LogOut,
  CheckCircle, Lock, Globe,
  Layers, AlertCircle, Info, BookOpen, X
} from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';

const SystemGuide = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const [activeRole, setActiveRole] = useState('ultra_admin');

  const handleLogout = () => {
    localStorage.removeItem('cop31_ultra_token');
    localStorage.removeItem('cop31_ultra_user');
    navigate('/giris');
  };

  const roles = [
    {
      id: 'ultra_admin',
      color: 'emerald',
      bgGrad: 'from-emerald-500 to-teal-600',
      lightBg: 'bg-purple-50',
      border: 'border-purple-200',
      textColor: 'text-purple-700',
      badgeBg: 'bg-purple-100',
      icon: Shield,
      label: 'Ultra Admin',
      zone: null,
      subtitle: tr ? 'Platform Süper Yöneticisi' : 'Platform Super Administrator',
      description: tr
        ? 'Tüm sistemi yöneten, en yüksek yetkiye sahip kullanıcı tipidir. Sadece teknik ekip tarafından atanır. Sistemde yalnızca 1 adet Ultra Admin bulunur.'
        : 'The user type with the highest authority managing the entire system. Assigned only by the technical team. There is only 1 Ultra Admin in the system.',
      loginPath: tr ? '/giris → Ultra Admin sekmesi' : '/giris → Ultra Admin tab',
      credentials: tr ? 'E-posta + Şifre' : 'Email + Password',
      permissions: tr ? [
        'Tüm kullanıcıları görme, düzenleme ve silme',
        'Blue / Green Zone ve Tedarikçi başvurularını onaylama/reddetme',
        'Stand Layout (SVG Grid) oluşturma ve düzenleme',
        'Tüm siparişleri ve teklifleri görüntüleme',
        'Platform ayarlarını (etkinlik tarihleri, email şablonları) yönetme',
        'Ürün kategorileri, birimler, şablonlar, renk/desen yönetimi',
        'E-posta şablonları oluşturma ve düzenleme',
        'Tüm MongoDB koleksiyonlarını görüntüleme',
        'Satış raporları ve ziyaretçi istatistikleri',
        'Platform kurallarını yönetme',
      ] : [
        'View, edit, and delete all users',
        'Approve/reject Blue/Green Zone and Supplier applications',
        'Create and edit Stand Layouts (SVG Grid)',
        'View all orders and quotes',
        'Manage platform settings (event dates, email templates)',
        'Manage product categories, units, templates, color/patterns',
        'Create and edit email templates',
        'View all MongoDB collections',
        'Sales reports and visitor statistics',
        'Manage platform rules',
      ],
      restrictions: tr ? [] : [],
      zones: [],
    },
    {
      id: 'customer_admin',
      color: 'blue',
      bgGrad: 'from-blue-500 to-indigo-600',
      lightBg: 'bg-blue-50',
      border: 'border-blue-200',
      textColor: 'text-blue-700',
      badgeBg: 'bg-blue-100',
      icon: Building2,
      label: tr ? 'Müşteri Admin' : 'Customer Admin',
      zone: 'Blue Zone / Green Zone',
      subtitle: tr ? 'Firma Yöneticisi (Katılımcı)' : 'Company Administrator (Participant)',
      description: tr
        ? 'COP 31 etkinliğine katılan firmaları temsil eden ana kullanıcıdır. Ultra Admin onayından sonra sisteme erişebilir. Blue Zone veya Green Zone\'a göre farklı kurallara tabidir.'
        : 'The main user representing companies participating in the COP 31 event. Can access the system after Ultra Admin approval. Subject to different rules depending on Blue Zone or Green Zone.',
      loginPath: tr ? '/giris → Blue Zone veya Green Zone sekmesi' : '/giris → Blue Zone or Green Zone tab',
      credentials: tr ? 'E-posta + Şifre (onay sonrası oluşturulur)' : 'Email + Password (created after approval)',
      permissions: tr ? [
        'Kendi stand layout\'unu görüntüleme',
        'Tedarikçi ürün ve hizmetlerini satın alma / teklif isteme',
        'Sepet yönetimi ve sipariş geçmişi',
        'Alt kullanıcı (Sub User) davet etme ve yönetme',
        'Firma profilini düzenleme',
        'Sipariş tekliflerini kabul etme veya reddetme',
      ] : [
        'View own stand layout',
        'Purchase supplier products/services or request quotes',
        'Cart management and order history',
        'Invite and manage sub-users',
        'Edit company profile',
        'Accept or reject order quotes',
      ],
      restrictions: tr ? [
        'Başka firmaların verilerine erişemez (tenant izolasyonu)',
        'Stand layout\'u sadece Ultra Admin oluşturabilir',
      ] : [
        'Cannot access other companies\' data (tenant isolation)',
        'Stand layout can only be created by Ultra Admin',
      ],
      zones: [
        {
          name: 'Blue Zone',
          color: 'blue',
          desc: tr
            ? 'Ülke temsilcisi — ülke başına yalnızca 1 adet kayıt alınır. UNFCCC akreditasyonu veya yeni başvuru ile kayıt.'
            : 'Country representative — only 1 registration per country. Registration via UNFCCC accreditation or new application.',
        },
        {
          name: 'Green Zone',
          color: 'emerald',
          desc: tr
            ? 'Şirket kaydı — aynı ülkeden birden fazla firma kaydolabilir. Sınırsız kayıt.'
            : 'Company registration — multiple companies from the same country can register. Unlimited registrations.',
        },
      ],
    },
    {
      id: 'supplier_admin',
      color: 'amber',
      bgGrad: 'from-amber-500 to-orange-600',
      lightBg: 'bg-amber-50',
      border: 'border-amber-200',
      textColor: 'text-amber-700',
      badgeBg: 'bg-amber-100',
      icon: Package,
      label: tr ? 'Tedarikçi Admin' : 'Supplier Admin',
      zone: 'Supplier Zone',
      subtitle: tr ? 'Ürün & Hizmet Sağlayıcısı' : 'Product & Service Provider',
      description: tr
        ? 'Etkinliğe ürün veya hizmet sunan firma yöneticisidir. /tedarikci-kayit sayfasından başvurur, Ultra Admin onayından sonra erişim kazanır.'
        : 'The company manager offering products or services to the event. Applies from /tedarikci-kayit page, gains access after Ultra Admin approval.',
      loginPath: tr ? '/giris → Supplier sekmesi' : '/giris → Supplier tab',
      credentials: tr ? 'E-posta + Şifre (onay sonrası aktif olur)' : 'Email + Password (activated after approval)',
      permissions: tr ? [
        'Ürün ve hizmet kataloğu oluşturma / düzenleme (CRUD)',
        'Ürün görselleri yükleme (S3 depolama, max 5/ürün)',
        'Varyant oluşturma ve fiyatlandırma (Günlük/Etkinlik/Sabit)',
        'Stok yönetimi (Katı/Esnek/Takipsiz politika)',
        'Gelen siparişleri ve teklifleri görüntüleme',
        'Teklif gönderme ve sipariş durumunu güncelleme',
        'Belge yükleme (PDF, DOC, görsel)',
        'Sepet rezervasyon sürelerini takip etme',
      ] : [
        'Create/edit product and service catalog (CRUD)',
        'Upload product images (S3 storage, max 5/product)',
        'Create variants and pricing (Daily/Event/Flat)',
        'Stock management (Strict/Flexible/Untracked policy)',
        'View incoming orders and quotes',
        'Submit quotes and update order status',
        'Upload documents (PDF, DOC, image)',
        'Track cart reservation times',
      ],
      restrictions: tr ? [
        'Sadece kendi ürün ve siparişlerini görebilir',
        'Başka tedarikçilerin verilerine erişemez',
        'Müşteri verilerine (layout, profil) erişemez',
      ] : [
        'Can only see own products and orders',
        'Cannot access other suppliers\' data',
        'Cannot access customer data (layout, profile)',
      ],
      zones: [],
    },
    {
      id: 'sub_user',
      color: 'purple',
      bgGrad: 'from-purple-500 to-violet-600',
      lightBg: 'bg-purple-50',
      border: 'border-purple-200',
      textColor: 'text-purple-700',
      badgeBg: 'bg-purple-100',
      icon: Users,
      label: tr ? 'Alt Kullanıcı' : 'Sub User',
      zone: tr ? 'Müşteri altında' : 'Under Customer',
      subtitle: tr ? 'Firma Çalışanı / Davetli Kullanıcı' : 'Company Employee / Invited User',
      description: tr
        ? 'Müşteri Admin tarafından e-posta ile davet edilen, aynı firmaya bağlı çalışandır. Davet linki ile profilini tamamlar, kategori atanır. Müşteri Admin istediği zaman aktif/pasif yapabilir.'
        : 'An employee linked to the same company, invited by email by the Customer Admin. Completes their profile via invitation link, category is assigned. Customer Admin can activate/deactivate at any time.',
      loginPath: tr ? '/giris → Blue Zone veya Green Zone sekmesi (aynı firma girişi)' : '/giris → Blue Zone or Green Zone tab (same company login)',
      credentials: tr ? 'E-posta + Davet linkiyle oluşturulan şifre' : 'Email + Password created via invitation link',
      permissions: tr ? [
        'Sipariş görüntüleme (salt okunur veya müşteri admin izniyle)',
        'Firma profilini görüntüleme',
        'Stand layout\'unu görüntüleme',
        'Kategori bazlı erişim (atanan kategoriye göre)',
      ] : [
        'View orders (read-only or with customer admin permission)',
        'View company profile',
        'View stand layout',
        'Category-based access (according to assigned category)',
      ],
      restrictions: tr ? [
        'Alt kullanıcı başka alt kullanıcı davet edemez',
        'Pasif yapılırsa giriş yapamaz (403 hatası)',
        'Davet 7 gün içinde kabul edilmezse sona erer',
      ] : [
        'Sub users cannot invite other sub users',
        'Cannot log in if deactivated (403 error)',
        'Invitation expires if not accepted within 7 days',
      ],
      zones: [],
    },
  ];

  const activeRoleData = roles.find(r => r.id === activeRole);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        {/* Top Nav */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-6 flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <BookOpen className="h-4 w-4 text-purple-700" />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900">{tr ? 'Sistem Rehberi' : 'System Guide'}</h1>
                <p className="text-xs text-gray-400">{tr ? 'Kullanıcı rolleri ve yetki yapısı' : 'User roles and permission structure'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle variant="admin" />
              <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-full text-sm text-gray-600 hover:bg-gray-50">
                <LogOut className="h-4 w-4" />{tr ? 'Çıkış' : 'Logout'}
              </button>
            </div>
          </div>
        </nav>

        <main className="flex-1 overflow-y-auto p-8">
          <div className="max-w-6xl mx-auto space-y-8">
            <PageIntro {...getPageIntro('system-guide', language)} />

            {/* Header */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{tr ? 'Kullanıcı Rolleri & Yetki Yapısı' : 'User Roles & Permission Structure'}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {tr
                  ? 'COP 31 platformunda 4 farklı kullanıcı tipi bulunmaktadır. Her tip farklı yetkiye ve erişim seviyesine sahiptir.'
                  : 'The COP 31 platform has 4 different user types. Each type has different permissions and access levels.'}
              </p>
            </div>

            {/* Role Selector Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {roles.map(role => {
                const Icon = role.icon;
                const isActive = activeRole === role.id;
                return (
                  <button
                    key={role.id}
                    onClick={() => setActiveRole(role.id)}
                    className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                      isActive
                        ? `border-${role.color}-400 bg-${role.color}-50 shadow-md`
                        : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
                    }`}
                  >
                    <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${role.bgGrad} flex items-center justify-center mb-3`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <p className="font-bold text-gray-900 text-sm">{role.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">{role.subtitle}</p>
                    {isActive && (
                      <div className={`absolute top-2 right-2 h-2 w-2 rounded-full bg-${role.color}-500`} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Detail Panel */}
            {activeRoleData && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Left — Overview */}
                <div className="lg:col-span-1 space-y-4">
                  {/* Role Card */}
                  <div className={`rounded-2xl bg-gradient-to-br ${activeRoleData.bgGrad} p-6 text-white`}>
                    <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                      {React.createElement(activeRoleData.icon, { className: 'h-6 w-6 text-white' })}
                    </div>
                    <h3 className="text-xl font-bold">{activeRoleData.label}</h3>
                    <p className="text-sm text-white/80 mt-1">{activeRoleData.subtitle}</p>
                    {activeRoleData.zone && (
                      <span className="inline-block mt-3 px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
                        {activeRoleData.zone}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <div className={`rounded-2xl border ${activeRoleData.border} ${activeRoleData.lightBg} p-4`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Info className={`h-4 w-4 ${activeRoleData.textColor}`} />
                      <span className={`text-sm font-semibold ${activeRoleData.textColor}`}>{tr ? 'Tanım' : 'Description'}</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{activeRoleData.description}</p>
                  </div>

                  {/* Login Info */}
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{tr ? 'Giriş Bilgisi' : 'Login Info'}</p>
                    <div className="flex items-start gap-2.5">
                      <Globe className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">{tr ? 'Giriş Sayfası' : 'Login Page'}</p>
                        <p className="text-sm font-medium text-gray-800">{activeRoleData.loginPath}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Lock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">{tr ? 'Kimlik Doğrulama' : 'Authentication'}</p>
                        <p className="text-sm font-medium text-gray-800">{activeRoleData.credentials}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right — Permissions & Details */}
                <div className="lg:col-span-2 space-y-4">

                  {/* Permissions */}
                  <div className="rounded-2xl border border-gray-100 bg-white p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle className={`h-5 w-5 ${activeRoleData.textColor}`} />
                      <h4 className="font-bold text-gray-900">{tr ? 'Yetkiler & Yapabilecekleri' : 'Permissions & Capabilities'}</h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {activeRoleData.permissions.map((perm, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                          <div className={`h-5 w-5 rounded-full ${activeRoleData.badgeBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <CheckCircle className={`h-3 w-3 ${activeRoleData.textColor}`} />
                          </div>
                          <span className="text-sm text-gray-700 leading-snug">{perm}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Restrictions */}
                  {activeRoleData.restrictions.length > 0 && (
                    <div className="rounded-2xl border border-red-100 bg-red-50 p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <h4 className="font-bold text-red-800">{tr ? 'Kısıtlamalar' : 'Restrictions'}</h4>
                      </div>
                      <div className="space-y-2">
                        {activeRoleData.restrictions.map((r, i) => (
                          <div key={i} className="flex items-start gap-2.5">
                            <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <X className="h-3 w-3 text-red-500" />
                            </div>
                            <span className="text-sm text-red-700">{r}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Zone Details (for customer_admin) */}
                  {activeRoleData.zones.length > 0 && (
                    <div className="rounded-2xl border border-gray-100 bg-white p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Layers className="h-5 w-5 text-gray-500" />
                        <h4 className="font-bold text-gray-900">{tr ? 'Zone Farkları' : 'Zone Differences'}</h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {activeRoleData.zones.map((zone, i) => (
                          <div key={i} className={`p-4 rounded-xl border-2 ${zone.color === 'blue' ? 'border-blue-200 bg-blue-50' : 'border-purple-200 bg-purple-50'}`}>
                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold mb-2 ${zone.color === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                              <div className={`h-2 w-2 rounded-full ${zone.color === 'blue' ? 'bg-blue-500' : 'bg-purple-600'}`} />
                              {zone.name}
                            </div>
                            <p className="text-sm text-gray-700 leading-snug">{zone.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Summary Table */}
            <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h4 className="font-bold text-gray-900">{tr ? 'Hızlı Karşılaştırma' : 'Quick Comparison'}</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Kullanıcı Tipi' : 'User Type'}</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Zone' : 'Zone'}</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Kayıt Yöntemi' : 'Registration Method'}</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Onay Gerekli mi?' : 'Approval Required?'}</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tr ? 'Adet Sınırı' : 'Count Limit'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[
                      { role: 'Ultra Admin', zone: '—', reg: tr ? 'Sistem tarafından atanır' : 'Assigned by system', approval: tr ? 'Hayır' : 'No', limit: '1' },
                      { role: tr ? 'Müşteri Admin (Blue)' : 'Customer Admin (Blue)', zone: 'Blue Zone', reg: tr ? 'UNFCCC veya Yeni Başvuru' : 'UNFCCC or New Application', approval: tr ? 'Evet' : 'Yes', limit: tr ? 'Ülke başına 1' : '1 per country' },
                      { role: tr ? 'Müşteri Admin (Green)' : 'Customer Admin (Green)', zone: 'Green Zone', reg: tr ? 'Yeni Başvuru' : 'New Application', approval: tr ? 'Evet' : 'Yes', limit: tr ? 'Sınırsız' : 'Unlimited' },
                      { role: tr ? 'Tedarikçi Admin' : 'Supplier Admin', zone: 'Supplier Zone', reg: tr ? '/tedarikci-kayit formu' : '/tedarikci-kayit form', approval: tr ? 'Evet' : 'Yes', limit: tr ? 'Sınırsız' : 'Unlimited' },
                      { role: tr ? 'Alt Kullanıcı' : 'Sub User', zone: tr ? 'Müşteri altında' : 'Under Customer', reg: tr ? 'Email daveti (Müşteri Admin)' : 'Email invite (Customer Admin)', approval: tr ? 'Hayır (davet linki)' : 'No (invite link)', limit: tr ? 'Müşteri başına sınırsız' : 'Unlimited per customer' },
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3.5 font-medium text-gray-900">{row.role}</td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.zone === 'Blue Zone' ? 'bg-blue-100 text-blue-700' :
                            row.zone === 'Green Zone' ? 'bg-purple-100 text-purple-700' :
                            row.zone === 'Supplier Zone' ? 'bg-amber-100 text-amber-700' :
                            row.zone === '—' ? 'bg-gray-100 text-gray-600' :
                            'bg-purple-100 text-purple-700'
                          }`}>{row.zone}</span>
                        </td>
                        <td className="px-5 py-3.5 text-gray-600">{row.reg}</td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${row.approval === 'Evet' || row.approval === 'Yes' ? 'text-orange-600' : 'text-purple-700'}`}>
                            {(row.approval === 'Evet' || row.approval === 'Yes')
                              ? <AlertCircle className="h-3.5 w-3.5" />
                              : <CheckCircle className="h-3.5 w-3.5" />}
                            {row.approval}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-gray-600">{row.limit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default SystemGuide;
